// Analytics: Comms (Calls + Inbox) feature
// Frontend events for the Heidi Comms (calls/voice agent + inbox) surface.
// Events are tracked via Snowplow + Datadog (Amplitude disabled).

export enum CommsEntryPoint {
  Sidenav = 'sidenav',
  DashboardTab = 'calls_dashboard_tab',
}

export enum CommsDashboardTab {
  Dashboard = 'dashboard',
  CallLogs = 'call-logs',
  Inbox = 'inbox',
  Patients = 'patients',
  Reminders = 'reminders',
  Objectives = 'objectives',
  Integrations = 'integrations',
}

export enum CommsSetupType {
  Terms = 'terms',
}

export enum CommsSetupAbandonReason {
  Dismissed = 'dismissed',
  Navigated = 'navigated',
}

export enum ObjectiveMode {
  Create = 'create',
  Edit = 'edit',
}

export enum ObjectiveConfigureSource {
  CreateFlow = 'create_flow',
  EditForm = 'edit_form',
}

export enum InboxFilterType {
  Status = 'status',
}

export enum InboxItemAction {
  MessageSent = 'message_sent',
  StatusChanged = 'status_changed',
  // Reserved for a future mark-as-read explicit action event.
  MarkedRead = 'marked_read',
}

export enum InboxSessionEndReason {
  TabSwitched = 'tab_switched',
  Unmounted = 'unmounted',
  Navigated = 'navigated',
}

export type CommsDashboardTabViewedEventProps = {
  tab: CommsDashboardTab;
  entry_point?: string;
};

export type CommsEntryPointClickedEventProps = {
  entry_point: CommsEntryPoint;
  is_calls_enabled: boolean;
};

export type CommsSetupStartedEventProps = {
  setup_type: CommsSetupType;
  total_steps: number;
};

export type CommsSetupStepCompletedEventProps = {
  setup_type: CommsSetupType;
  step_id: string;
  step_index: number;
  total_steps: number;
};

export type CommsSetupStepAbandonedEventProps = {
  setup_type: CommsSetupType;
  step_id?: string;
  step_index?: number;
  total_steps: number;
  reason?: CommsSetupAbandonReason;
};

export type CommsSetupCompletedEventProps = {
  setup_type: CommsSetupType;
  total_steps: number;
};

export type ObjectiveSelectedEventProps = {
  mode: ObjectiveMode;
  objective_id?: string;
  objective_name?: string;
  direction?: string;
};

export type ObjectiveRemovedEventProps = {
  objective_id: string;
};

export type ObjectiveConfiguredEventProps = {
  objective_id: string;
  source: ObjectiveConfigureSource;
  direction?: string;
};

export type ObjectiveViewedEventProps = {
  objective_id: string;
  objective_name?: string;
  direction?: string;
};

export type InboxSessionStartedEventProps = {
  entry_point?: string;
};

export type InboxSessionEndedEventProps = {
  duration_ms: number;
  reason: InboxSessionEndReason;
};

export type InboxItemViewedEventProps = {
  conversation_id: string;
  has_unread: boolean;
  unread_count?: number;
};

export type InboxItemUpdatedEventProps = {
  action: InboxItemAction;
  conversation_id?: string;
  new_status?: string;
  has_attachment?: boolean;
};

export type InboxFilterAppliedEventProps = {
  filter_type: InboxFilterType;
  filter_value: string | null;
};

// ---------------------------------------------------------------------------
// KB Learning (Objectives tab — scan / review / preview / apply gaps)
//
// Prop names are intentionally aligned with the BE event shapes documented in
// BACKEND_IMPLICATIONS.md so Snowplow / Amplitude tables join cleanly. The
// Snowplow handlers will no-op until snowtype regenerates with registered
// schemas; events still flow to Amplitude/Datadog via `trackEventV2`.

export type KbObjectivesTabViewedEventProps = {
  /** Number of gaps already pending on mount (drives idle → review jump). */
  pending_gap_count: number;
  has_pending_gaps: boolean;
};

export type KbScanButtonClickedEventProps = {
  /**
   * `re_scan` when fired from the empty-state CTA, `initial` otherwise.
   * Lets product distinguish "first ever scan" from manual refreshes.
   */
  trigger: 'initial' | 're_scan';
};

export type KbPreviewButtonClickedEventProps = {
  answered_count: number;
  dismissed_count: number;
  /** Total gaps presented on the review screen (denominator for funnel). */
  total_gap_count: number;
};

export type KbApplyButtonClickedEventProps = {
  /** Number of agents the FE is asking the BE to update. */
  agent_count: number;
};

export type KbApplySuccessViewedEventProps = {
  /**
   * Count of agents that successfully applied. We deliberately send a count
   * (not display names): agent names are often clinic/department-identifying
   * and must not reach third-party analytics (Amplitude/Snowplow). KB content,
   * answer text, and patient data are likewise never included on any KB event.
   */
  agent_count: number;
  /** Total knowledge gaps closed across all applied agents. */
  gaps_applied: number;
};
